import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
import type { CameraZone } from 'src/components/Camera/types/camera-zones';
import { open_external_link_android } from 'src/services/native_container_requests_android';
import { open_external_link } from 'src/services/native_container_requests_ios';
import { app, os } from 'src/stores/session/native-bridge';
import type { AvailableUnitInfo, AvailableUnits } from 'src/types/responses/usersAuthResponse';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
// Helper functions

export function makeNumbersPrimaryAndBold(numbers: string) {
	return numbers.replace(/-?(\d+(\.\d*)?|\.\d+)/, `<span class='text-primary text-bold'>$1</span>`);
}
export function makeRangePrimaryAndBold(numbers: string) {
	return numbers.replace(
		/(-?\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)*|-)/g,
		`<span class='text-primary text-bold'>$&</span>`,
	);
}

export function makeValueAndUnitsPrimaryAndBold(string: string) {
	let stringToArray = string.split(' ');
	let unit = stringToArray[stringToArray.length - 1];
	let value = stringToArray[stringToArray.length - 2];
	let rest = stringToArray.slice(0, stringToArray.length - 2).join(' ');
	return `${rest} <span class='text-primary text-bold'>${value} ${unit}</span>`;
}

export function capFirstChar(str: string) {
	if ('undefined' === typeof str) {
		// console.warn('Cannot format an empty string!');
		return str;
	}
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export const calculateTokenWidthByTitle = (title: string): number => {
	return Math.max(90, Math.ceil(title.length * 5.6) + 70);
};

export const firstCharCapOnly = (value: string): string => {
	if (!value) return '';

	return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

//this fn will work only for relay & light strings
export function translateAndCapitalise(str: string) {
	let strTranslated = get(format)(`${str}`);
	let capitalisedString = strTranslated.charAt(0).toUpperCase() + strTranslated.slice(1);
	return capitalisedString;
}

export function removeOrAddUnderscore(str: string, type: 'removeUnderscore' | 'addUnderscore'): string {
	if (type === 'removeUnderscore') {
		return str?.replace(/_/g, ' ');
	} else if (type === 'addUnderscore') {
		return str?.replace(/\s+/g, '_');
	}
}

/**
 * This fn transformes string time zone to get underscore, to remove underscore or exception like 'Antarctica/DumontDUrville'
 * @param timezones array
 * @returns array
 */
export const transformedTimeZones = (timezones) => {
	return timezones.map((tz) => {
		if (tz === 'Antarctica/DumontDUrville') {
			return {
				stringToVisualise: 'Antarctica/Dumont D`Urville',
				stringToSend: tz,
			};
		}
		return {
			stringToVisualise: removeOrAddUnderscore(tz, 'removeUnderscore'),
			stringToSend: tz,
		};
	});
};

export function trimLastChar(str: string) {
	if ('undefined' === typeof str) {
		console.warn('Cannot format an empty string!');
		return str;
	}
	return str.slice(0, -1);
}

/**
 * Converts time between seconds and minutes.
 *
 * This function takes a time value and a unit ('seconds' or 'minutes') and converts
 * the value to the corresponding time in the opposite unit. The result is rounded to one decimal place.
 *
 * @param {number} value - The time value to be converted. It can be in seconds or minutes depending on the unit specified.
 * @param {string} fromUnit - The unit of the provided value. Must be either 'seconds' or 'minutes'.
 *
 * @returns {string|number} - Returns the converted time value as a string with one decimal place.
 * If the input unit is invalid, it returns an error message string.
 *
 */
export function convertTime(value, fromUnit) {
	if (fromUnit === 'seconds') {
		return Number((value / 60).toFixed(1));
	} else if (fromUnit === 'minutes') {
		return Number((value * 60).toFixed(1));
	} else {
		return "Invalid unit. Use 'seconds' or 'minutes'.";
	}
}

export function formatSnakeCaseToTitle(str: string) {
	if ('undefined' === typeof str) {
		console.warn('Cannot format an empty string!');
		return str;
	}
	return str
		.split('_')
		.map((word) => capFirstChar(word))
		.join(' ');
}

/**
 * This fn replace space and '/' with '_', to look like a snakecase. For instance use it to create a key for translation.
 * @param str
 * @returns string
 */
export function formatTitleToSnakeCase(str: string) {
	if ('undefined' === typeof str) {
		console.warn('Cannot format an empty string!');
		return str;
	}
	return str.replace(/[\s+\/]/g, '_').toLowerCase();
}

export function formatAsSvelteFileTitle(str: string) {
	if ('undefined' === typeof str) {
		console.warn('Cannot format an empty string!');
		return str;
	}
	return str
		.split('_')
		.map((word) => capFirstChar(word))
		.join('');
}

export function mathEpsilon(num, fixed = 2) {
	return Number(num ? (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(fixed) : 0.0);
}

export function objectKeysToLowerCase(obj) {
	return Object.keys(obj).reduce((new_obj, key) => {
		let val = obj[key];
		let new_val = typeof val === 'object' ? objectKeysToLowerCase(val) : val;
		new_obj[key.toLowerCase()] = new_val;
		return new_obj;
	}, {});
}

//Todo: rethink this
export function convert(value, unit) {
	if (!unit) unit = '';

	if (value > 1000000 || value < -1000000) {
		value = value / 1000000;
		unit = 'M' + unit;
	}

	if (value > 999 || value < -999) {
		value = value / 1000;
		unit = 'k' + unit;
	}
	return mathEpsilon(value) + ' ' + unit;
}

export function convertValueAndUnit(
	value: number,
	unit: string,
	inKilo: boolean = false,
	inMega: boolean = false,
) {
	if (!unit) unit = '';

	if (inMega || value > 1000000 || value < -1000000) {
		value = value / 1000000;
		unit = 'M' + unit;
	}

	if (inKilo || value > 999 || value < -999) {
		value = value / 1000;
		unit = 'k' + unit;
	}

	return {
		value: mathEpsilon(value),
		unit: unit,
	};
}

export function convertCost(value: number, currency: string): string {
	if (value > 0) {
		value = Number((Math.ceil(value * 100) / 100).toFixed(2));
	}
	return value > 0 ? `${value} ${currency || ''}` : null;
}

//Todo: maybe rework as a class???
let timer;
export function debounce(callback, timeout) {
	clearTimeout(timer);
	timer = setTimeout(callback, timeout);
}

export function throttle(callback, interval) {
	let enableCall = true;

	return function (...args) {
		if (!enableCall) return;

		enableCall = false;
		callback.apply(this, args);
		setTimeout(() => (enableCall = true), interval);
	};
}

export const wait = (ms) => new Promise((res) => setTimeout(res, ms));

// export const range = (start, end, step) => Array.from((function* () {
// 	let iterationCount = 0;
// 	for (let i = start; i <= end; i += step) {
// 		iterationCount++;
// 		yield i;
// 	}
// 	return iterationCount;
// })());

export const blob_to_file = (theBlob: Blob, fileName: string): File => {
	let b: any = theBlob;

	//A Blob() is almost a File() - it's just missing the two properties below which we will add
	b.lastModified = new Date();
	b.name = fileName;

	//Cast to a File() type
	return <File>theBlob;
};

//CALCULATE % FOR DEVICE/ROOM FROM TOTAL ROOM/WMH -----------------------------------------------
export const percentageCalc = (num, num2, checked) => {
	let inc;
	if (num2 && num) {
		inc = checked ? ((num2 / (num / 1000)) * 24) / 365 : (num / num2) * 100;
		return inc;
	}
};

/**
 * checks boolean data in log
 * it may be on/off value or  true/false for sensors detection data (smoke, water)
 * @param object = object of consumption values (store) to extract the total value from
 * @param prop = the prop/type of consumption total to add
 */
export const consumption_add_total = (object, prop) => {
	if (Object.keys(object).length === 0) {
		return 0;
	}
	return Object.values(object).reduce((a, b) => {
		return { [prop]: a[prop] + b[prop] };
	})[prop];
};

// Calculate temperature color from kelvins -------------------------------------
export function kelvin_to_rgb(temp, out?) {
	if (!Array.isArray(out)) {
		out = [0, 0, 0];
	}

	temp = temp / 100;

	let red, blue, green;

	if (temp <= 66) {
		red = 255;
	} else {
		red = temp - 60;
		red = 329.698727466 * Math.pow(red, -0.1332047592);
		if (red < 0) {
			red = 0;
		}
		if (red > 255) {
			red = 255;
		}
	}

	if (temp <= 66) {
		green = temp;
		green = 99.4708025861 * Math.log(green) - 161.1195681661;
		if (green < 0) {
			green = 0;
		}
		if (green > 255) {
			green = 255;
		}
	} else {
		green = temp - 60;
		green = 288.1221695283 * Math.pow(green, -0.0755148492);
		if (green < 0) {
			green = 0;
		}
		if (green > 255) {
			green = 255;
		}
	}

	if (temp >= 66) {
		blue = 255;
	} else {
		if (temp <= 20) {
			blue = 0;
		} else {
			blue = temp - 10;
			blue = 138.5177312231 * Math.log(blue) - 305.0447927307;
			if (blue < 0) {
				blue = 0;
			}
			if (blue > 255) {
				blue = 255;
			}
		}
	}

	out[0] = Math.ceil(red);
	out[1] = Math.ceil(green);
	out[2] = Math.ceil(blue);
	return out;
}

// TIME TRANSLATOR USED IN COMPONENTS/TIMEPICKERWRAPPER
export function time_translate(time) {
	let hours = time.split(':')[0];
	let minutes = time.split(':')[1];
	return [hours, minutes];
}

export const percent_from_value_range = (val, range) => {
	return Math.round(((val - range[0]) / (range[1] - range[0])) * 100);
};

export const value_from_percent_range = (percent, range) => {
	return Math.round(((range[1] - range[0]) / 100) * percent) + range[0];
};

export const convert_array_to_object = (array, key) => {
	const initial_value = {};
	return array.reduce((obj, item) => {
		return { ...obj, [item[key]]: item };
	}, initial_value);
};

export const ms_to_time = (duration) => {
	let seconds: string | number = Math.floor((duration / 1000) % 60);
	let minutes: string | number = Math.floor((duration / (1000 * 60)) % 60);
	let hours: string | number = Math.floor((duration / (1000 * 60 * 60)) % 24);

	hours = hours < 10 ? '0' + hours : hours;
	minutes = minutes < 10 ? '0' + minutes : minutes;
	seconds = seconds < 10 ? '0' + seconds : seconds;

	return hours + ':' + minutes + ':' + seconds;
};

export const to_hh_mm_ss = (sec) => {
	let hours: string | number = Math.floor(sec / 3600);
	let minutes: string | number = Math.floor((sec - hours * 3600) / 60);
	let seconds: string | number = sec - hours * 3600 - minutes * 60;

	hours = hours < 10 ? '0' + hours : hours;
	minutes = minutes < 10 ? '0' + minutes : minutes;
	seconds = seconds < 10 ? '0' + seconds : seconds;

	return hours + ':' + minutes + ':' + seconds;
};

export function formatDate(date) {
	var d = new Date(date),
		month = '' + (d.getMonth() + 1),
		day = '' + d.getDate(),
		year = d.getFullYear();

	if (month.length < 2) month = '0' + month;
	if (day.length < 2) day = '0' + day;

	return [year, month, day].join('-');
}

export function mathNormalize(n: number, min = 0, max = 1) {
	return n < min ? min : n > max ? max : n;
}

/** ---- DEEP COPY OBJECT FUNCTIONS ---- */
/** !!!!!!!! MAY BE THE SECURE WAY is lodash */
//TODO: think on better way to handle deep copy to avoid mutating of the templates and stores
/** Ref:
 * https://blog.logrocket.com/methods-for-deep-cloning-objects-in-javascript/
 */
export function deepCopy(obj) {
	return JSON.parse(JSON.stringify(obj));
}

export function _deepCopyArr(arr = []) {
	let clone = [];
	for (let el of arr) {
		if (typeof el === 'object') {
			clone.push(el?.hasOwnProperty('length') ? _deepCopyArr(el) : _deepClone(el));
		} else {
			clone.push(el);
		}
	}
	return clone;
}

//copy arrays and objects
export function _deepClone(obj) {
	let clone = {};
	for (let key in obj) {
		// console.log('iterate ', typeof obj[key])
		if (obj.hasOwnProperty(key)) {
			if (typeof obj[key] === 'object') {
				clone[key] = obj[key]?.hasOwnProperty('length')
					? _deepCopyArr(obj[key])
					: _deepClone(obj[key]);
			} else {
				clone[key] = obj[key];
			}
		}
	}
	return clone;
}
/** --- END DEEP COPY OBJECT FUNCTIONS --- */

/**
 * router/navigation functions
 */

export function backTo(window, history, goTo = '/', steps = 0, callback) {
	console.log('GO TO ', goTo, steps);
	history.replaceState({ page: 1 }, '', JSON.stringify(window.history.go(steps)));
	// history.replaceState({page:2}, 'room ', `/room/${room}`);
	console.log('after use replace ', JSON.stringify(history.state), JSON.stringify(window.history));
	callback(goTo);
}

export function calculateHowStepsBack(current, start) {
	return (Number(current) - Number(start) + 1) * -1;
}

/**
 * Scene's active time and
 * Temperature schemes /thermostats custom intervals
 *  calculate and validate helpers
 */
export const real_duration_from_negative = (duration = -1100) => {
	/**
	 * This is a required step to calculate duration
	 * for intervals as [20:00, 02:00] with duration in the next day,
	 * where the diff_minutes function returns the difference between (20 - 02)*(-1) => 18h*60*(-1)
	 * not the real value which is 6h*60,
	 * so the conversion is 18h*60*(-1) + 1440 = -1080 + 1440 = 360min = 6h*60;
	 */

	if (Number(duration) < 0) {
		//because of some calculation misstake
		return Number(duration) + 1440;
	}
};

/**
 *returns difference in minutes between dates
 */
const diff_minutes = (dt2, dt1) => {
	var diff = (dt2.getTime() - dt1.getTime()) / 1000;

	diff /= 60;

	return Math.round(diff);
};
/**
 *
 * @param time ['20:00', '22:00']
 * @returns Number (minutes)
 */
export const _calculate_duration = (time) => {
	let splitStart = time[0].split(':').map((e) => parseInt(e));
	let splitEnd = time[1].split(':').map((e) => parseInt(e));

	let dtStart = new Date(2021, 3, 1, splitStart[0], splitStart[1]);
	let dtEnd = new Date(2021, 3, 1, splitEnd[0], splitEnd[1]);

	const duration = diff_minutes(dtEnd, dtStart);

	return duration;
};

export const check_times = (existing_interval, new_interval, new_duration) => {
	// console.log('check intervals ', new_interval, existing_interval);
	let max_allowed_time = _calculate_duration([new_interval[0], existing_interval[0]]);
	let end_existing_to_start_new = 0;

	//here the new it comes entirely before the existing interval
	if (max_allowed_time > 0 && max_allowed_time >= new_duration) {
		return true;
	}

	// if the above is not true, it means that the interval starts in the existing
	end_existing_to_start_new = _calculate_duration([existing_interval[1], new_interval[0]]);

	//the new interval ends in the existing one, so it is invalid;
	if (max_allowed_time <= 0 && end_existing_to_start_new < 0) {
		return false;
	}

	//starts and ends after existing
	if (max_allowed_time < 0 && end_existing_to_start_new >= 0) return true;

	return false;
};

export const _validate_one_day_interval = (new_interval, exist, new_duration) => {
	let { time, active } = new_interval;
	let { custom_days, custom_times } = exist;
	let exist_interval_duration = 0,
		isValid = true,
		end_double_interval_days = [];
	//custom days store all interval days, something like [[1,2,3,4], [6], [4,5]]
	//custom times store all time of intervals, [[10:00, 15:00], [23:40, 06:00], [08:00, 12:00]]
	for (let i = 0; i < custom_days.length; i++) {
		/**
		 * 1. exist_duration > 0 => in one day interval
		 * 2. exist_duration < 0 => in two days interval
		 * try to start the new one :
		 * - it is called max_allowed_time for the new interval before start existing
		 */
		exist_interval_duration = _calculate_duration(custom_times[i]);

		// console.log('EXISTING INTERVAL DUR ', exist_interval_duration);

		if (exist_interval_duration < 0) {
			//create a new array with the days when double interval ends
			// ecause we need to check them
			end_double_interval_days = [
				...custom_days[i].map((e) => (Number(e) + 1 > 6 ? 0 : Number(e) + 1)),
			];
		}
		for (let _d of active) {
			if (exist_interval_duration > 0) {
				//HERE both intervals are in one day, so
				//ON first FALSE response return with false - INVALID interval,
				// it the IsValid is still true - continue;
				if (!custom_days[i].includes(_d)) continue;

				isValid = check_times(custom_times[i], time, new_duration);
				if (isValid) {
					continue;
				} else {
					console.log(
						'Validation faild in single to single ',
						_d,
						custom_times[i],
						new_interval,
					);
					return false;
				}
			} else {
				//EXISITING INTERVAL DURATION is negative value, so
				// interval starts in one day and ends in next [22:30, 06:30]
				//we split interval for easy check
				let split_existing_interval = {
					start: [custom_times[i][0], '23:59'],
					end: ['00:00', custom_times[i][1]],
				};

				//we start with true value for both intervals (from split existing)
				//if the day we check does not contains in one of the day intervals
				// it it valid, so no need to check it
				let validateStart = true,
					validateEnd = true;

				//but if the day includes in start days interval
				// we need to validate that the new one ends before existing to start
				// so new interval need to ends here split_exisiting_interval.start[0]
				if (custom_days[i].includes(_d)) {
					validateStart = check_times(split_existing_interval.start, time, new_duration);
				}
				//we check the day which is in the next days of the existing interval (array with days, when existing interval ends)
				// here the new interval need to start after end of interval split_exisiting_interval.end[1]
				if (end_double_interval_days.includes(_d)) {
					validateEnd = check_times(split_existing_interval.end, time, new_duration);
				}
				//if some of the above is FALSE (on first false retured day)
				//return with FALSE
				if (!(validateStart && validateEnd)) {
					console.log('Failed in single to double check ', validateStart, validateEnd);
					return false;
				}
				//else next check
			}
		}
	}
	// console.log('the interval is valid ', isValid);
	return isValid;
};

export const validate_custom_intervals = (
	new_interval_date = { time: ['20:00', '06:00'], active: [2, 5] },
	already_exist_intervals = {
		custom_days: [
			[1, 2],
			[6, 7],
		],
		custom_times: [
			['20:00', '21:00'],
			['05:00', '07:00'],
		],
	},
) => {
	/**
	 * 1. New interval duration
	 * - ===0 - it is something like this [10:00, 10:00] - set as invalid
	 * - >0 - interval starts and ends in the same day
	 * - <0 - interval duration starts in one day and ends in the next
	 */
	let { time } = new_interval_date;
	let new_interval_duration = _calculate_duration(time);
	if (new_interval_duration === 0) return false;
	if (new_interval_duration > 0) {
		// console.log("In day interval valaidation ", new_interval_date);
		//validate in one day new interval
		return _validate_one_day_interval(
			new_interval_date,
			already_exist_intervals,
			new_interval_duration,
		);
	} else {
		//to check is it valuid interval
		//split it in two new and check the days when start in and the days when ends ;
		// if some check fails - interval is not valid
		let start_interval = {
			time: [new_interval_date.time[0], '23:59'],
			active: [...new_interval_date.active],
		};
		let end_interval = {
			time: ['00:00', new_interval_date.time[1]],
			active: [...new_interval_date.active.map((e) => (Number(e) + 1 > 6 ? 0 : Number(e) + 1))],
		};
		//return ifStartValid && endValid (as the above)
		let isStartValid = _validate_one_day_interval(
			start_interval,
			already_exist_intervals,
			_calculate_duration(start_interval.time),
		);
		let isEndValid = _validate_one_day_interval(
			end_interval,
			already_exist_intervals,
			_calculate_duration(end_interval.time),
		);

		return isStartValid && isEndValid;
	}
};

export const validate_thermostat_object = (thermostat) => {
	if (
		!thermostat._meta ||
		!thermostat._meta.room ||
		!thermostat._meta.type ||
		!thermostat._meta.name
	) {
		return false;
	}

	if (!thermostat.tocool.blk || thermostat?.tocool?.blk.length === 0) {
		return false;
	}
	if (!thermostat.toheat.blk || thermostat?.toheat?.blk.length === 0) {
		return false;
	}
	if (!thermostat.tschem || !thermostat.tschem.r || thermostat.tschem.r === '') {
		return false;
	}
	if (typeof thermostat.deftemp === 'undefined' || typeof thermostat.ths === 'undefined') {
		return false;
	}
	return true;
};

export const _calculate_duration_in_ms = (_min = -100) => {
	let min = _min;
	// console.log('Minutes ', min);
	if (min < 0) {
		min = 1440 + min; //real_duration_from_negative(min);
	}

	return min * 60 * 1000;
};
// /**
//  * End Temperature schemes/ thermostat helpers
//  */

//  const calculate_duration = (time) => {
// 	let value = {startTime: time[0], endTime: time[1]};

// 	let splitStart = value.startTime.split(":").map(el => parseInt(el));
// 	let splitEnd = value.endTime.split(":").map(el=> parseInt(el));
// 	let hours = parseInt(splitEnd[0] - splitStart[0]);
// 	let min = parseInt((60-splitStart[1]) + splitEnd[1])%60;
// 	// console.log(hours, min);
// 	const duration = (hours*60) + min;
// 	// value.duration = duration;
// 	return duration;

// };

export function number_to_letter(value, from_zero) {
	return (value + (from_zero ? 10 : 9)).toString(36).toUpperCase();
}

/**
 * 
const diff_minutes = (dt2, dt1) =>  {

  var diff =(dt2.getTime() - dt1.getTime()) / 1000;
  diff /= 60;

  return Math.round(diff);
  
};
const _calculate_duration = (time) => {
	let value = {startTime: time[0], endTime: time[1]};
	    
	let splitStart = value.startTime.split(":").map(el => parseInt(el));
	let splitEnd = value.endTime.split(":").map(el=> parseInt(el));

	// if time interval looks like this ['20:20', '02:02']
	let dtStart = new Date(2021, 3, 1, splitStart[0], splitStart[1]);
	let dtEnd = new Date(2021, 3, 1, splitEnd[0], splitEnd[1]);
    
	const duration = diff_minutes(dtEnd, dtStart);

	return duration;
};


console.log("one day interval ",_calculate_duration(["12:12", "14:14"]));

console.log("in two day intervals ",_calculate_duration(["23:00", "06:23"]));

console.log("it is also in two day interval",_calculate_duration(["12:00", "10:00"]));

console.log('duration between end of interval and start of new one ', _calculate_duration(["10:00", "10:00"]));

> "one day interval " 122
> "in two day intervals " -997
> "it is also in two day interval" -120
> "duration between end of interval and start of new one " 0
 */

//#region timezone converter
/**
 * @function convert_time_zone is used inside device_data_helpers to visualize latest devices falling to sleep :D but with users timezone
 * @param data incoming data, maybe only status._updated
 * @returns date string like Sat Oct 02 2021 08:11:00 GMT+0300 (Eastern European Summer Time) this one
 */
export function convert_time_zone(data) {
	let income_data = new Date(data.replace(/\s+/g, 'T')); //this is for Safari
	let timeOffset = new Date().getTimezoneOffset(); //this is local offset
	let outcome_data = new Date(income_data.getTime() - timeOffset * 60 * 1000); //device report date minus offset

	return outcome_data;
}
//#endregion

//#region devices array local/cloud
export const remove_device_from_array = (array, id) => {
	array.map((dev, index) => {
		if (dev.id === id) {
			array.splice(index, 1);
		}
	});

	return array;
};

export const remove_duplicates_from_array = (array) => {
	let output = [];
	array.forEach((device, index) => {
		if (output.includes(device.id)) {
			array.splice(index, 1);
		} else {
			output.push(device.id);
		}
	});
	return array;
};
//#endregion

export const hex_to_decimal = (value) => {
	return parseInt(value, 16).toString();
};
export const decimal_to_hex = (value) => {
	const hex = parseInt(value, 10).toString(16);
	return hex.length % 2 ? '0' + hex : hex; //add 0 if value length is odd, which means that it begins with 0 but hex to string(16) will remove it
};
export const stringToHex = (value) => '0x' + value.toString(16).toUpperCase().padStart(4, 0);

export const math_circular = (number: number, length: number): number => {
	number = number % length || 0;
	if (number < 0) return length + number;
	else if (number >= length) return number - length;
	return number;
};

export const valueOutOfRange = (value: number, min: number, max: number) => {
	return isNaN(value) || Number(value) < min || Number(value) > max;
};

export const valueOutOfDecimalRange = (value: string, digits = 2) => {
	if (RegExp(`^-?\\d+\\.\\d{${digits},}$`).test(value)) {
		const factor = Math.pow(10, digits - 1);
		return get(format)('decimal_error_description', {
			values: {
				min: Math.floor(Number(value) * factor) / factor,
				max: Math.ceil(Number(value) * factor) / factor,
			},
			default: 'Please enter a valid value.The two nearest valid values are {min} and {max}',
		});
	}
	return '';
};

export const short_id = (id) => id.slice(-6); //this is used inside range extender for now. Must support older devices

export const average = (array) => array.reduce((pv, cv) => pv + cv, 0) / array.length;

export const sum = (array) => array.reduce((pv, cv) => pv + cv, 0);

export const flatten = (array) => array.reduce((pv, cv) => pv.concat(cv), []);

export const format_temp_units = (units) => {
	if (typeof units === 'string' && (units.match(/C/) || units.match(/F/))) {
		return units.length === 2 ? units : '°' + units;
	}
	return '°C';
};

export const sortOn = (arr: any[], key = null, decs = false) => {
	arr?.sort((a, b) => {
		if (key !== null) {
			a = a[key];
			b = b[key];
		}
		let result = 0;
		if (!isNaN(a) && !isNaN(b)) result = a - b;
		else {
			const attrA = String(a).toLowerCase(),
				attrB = String(b).toLowerCase();
			if (attrA < attrB) result = -1;
			else if (attrA > attrB) result = 1;
		}
		return decs ? result * -1 : result;
	});
	return arr;
};

export const isValidURL = (url: string, includeLocalhost = false) => {
	if (includeLocalhost && /^(https?:\/\/localhost\/)/.test(url)) {
		return true;
	}
	return /(((https?|ftp|file):\/\/)|(www\.))([a-z0-9-]+)[.:]([a-z0-9-]+)/i.test(url);
};

export function parseQuerystring(querystring: string): {
	[key: string]: string;
} {
	let params = new URLSearchParams(querystring);
	let parsedQuery: { [key: string]: string } = {};
	for (let [key, value] of params.entries()) {
		parsedQuery[key] = value;
	}
	return parsedQuery;
}

export function alphabetToPosition(text) {
	// ES6 beacuse of 'parseInt'
	return [...text].map((a) => parseInt(a, 36) - 10).filter((a) => a >= 0);
}

export function indexToAlphabet(num = 1) {
	// ASCII value of first character
	const A = 'A'.charCodeAt(0);
	let numberToCharacter = (number) => {
		return String.fromCharCode(A + number);
	};
	return numberToCharacter(num);
}

export const colorLimited = (color: number | string, type = 'from') => {
	//this function is used to convert rgbw values for PlusRGBW gen 2 device
	//Color transform from percentage (i.e. 0-100) to full range (0-255)
	if (type === 'from') {
		return Math.round(parseFloat(color.toString()) * 2.55);
	}
	//Color transform from full range (i.e. 0-255) to percentage (0-100)
	if (type === 'to') {
		return Number((parseFloat(color.toString()) / 2.55).toFixed(3));
	}
};

export const getErrorOrInfoMessageForMinAndMax = (
	value: number,
	min: number,
	max: number,
	translationsWithWatts: boolean = false,
	translationsWithMinutes: boolean = false,
) => {
	const isInvalid = valueOutOfRange(value, min, max);
	let translationLabel = 'min_max_error_description';
	let defaultForTranslation = 'Value must be between {min} and {max}.';

	if (translationsWithWatts) {
		translationLabel = 'min_max_error_description_watts';
		defaultForTranslation = 'Value must be between {min} W - {max} W.';
	}

	if (translationsWithMinutes) {
		translationLabel = 'min_max_error_description_minutes';
		defaultForTranslation = 'Value must be between {min} - {max} minutes.';
	}

	const validationData = {
		error: '',
		info: get(format)(translationLabel, {
			values: { min, max },
			default: defaultForTranslation,
		}),
	};

	if (isInvalid) {
		validationData.error = get(format)(translationLabel, {
			values: { min, max },
			default: defaultForTranslation,
		});
		validationData.info = '';
	}

	return validationData;
};

export const getErrorOrInfoMessageForMinAndMaxUnit = (
	value: number,
	min: number,
	max: number,
	unit: string = '',
): { error: string; info: string } => {
	if (min == null || max == null) return { error: '', info: '' };
	const validationData = getErrorOrInfoMessageForMinAndMax(value, min, max);
	if (unit && validationData?.info)
		validationData.info = validationData?.info.replace(/\.$/, '') + ` ${unit}.`;
	else if (unit && validationData?.error)
		validationData.error = validationData?.error.replace(/\.$/, '') + ` ${unit}.`;
	return validationData;
};

export const genCharArray = (number) => {
	const chars = [...Array(26).keys()].map((i) => String.fromCharCode(i + 97));
	chars.splice(number, chars.length - number);
	return chars;
};

// Returns password strength score in the interval [0, 100]
export const strengthValidation = (value: string, minLength = 6): number => {
	let score = 0;

	// Clear white spaces and replace duplicates of consecutive characters
	value = value.replace(/\s+/g, '').replace(/(.)\1{1,}/g, '$1');

	// Contains digit, lowercase alphabet, uppercase alphabet
	score += /[\d]/.test(value) ? 10 : 0;
	score += /[a-z]/.test(value) ? 10 : 0;
	score += /[A-Z]/.test(value) ? 10 : 0;

	// Find digits, lowercase letters, and uppercase letters
	// that are not located after other similar characters.
	score += (value.match(/([A-Z])(?=[a-z\d])|([a-z])(?=[A-Z\d])|(\d)(?=[a-zA-Z])/g)?.length || 0) * 10;

	// Find special characters
	score += (value.match(/([!@#$%^&*?_~])/g)?.length || 0) * 15;

	// Additional scoring for longer passwords
	if (score > 30) score += Math.min(value.length - minLength, 10) * 5;

	return Math.min(score, 100);
};

// Checks if the provided password includes all mandatory symbols
export const passwordHasAllMandatorySymbols = (passValue: string): boolean => {
	const lowercaseRegex = /[a-z]/;
	const uppercaseRegex = /[A-Z]/;
	const numberRegex = /[0-9]/;

	return (
		lowercaseRegex.test(passValue) && uppercaseRegex.test(passValue) && numberRegex.test(passValue)
	);
};

export const openExternalLink = (url: string) => {
	if (get(app)) {
		if (get(os) === 'IOS') {
			open_external_link(url);
		} else {
			open_external_link_android(url);
		}
	} else {
		window.open(url, '_blank');
	}
};

export const conditionsFunc = (prop: string, includeZero?: boolean, isDecimal?: boolean) => {
	return (
		isNaN(+prop) ||
		(includeZero ? Number(+prop) <= 0 : Number(+prop) < 0) ||
		(isDecimal ? !/^\d+(\.\d+)?$/.test(prop) : !/^[0-9]+$/.test(prop)) ||
		(!isDecimal && !Number.isSafeInteger(+prop))
	);
};

export const createHexToDecimalOrId = (id) => {
	//used om ui_actions_to_scene.ts
	return isNaN(parseInt(id, 16)) ? id : parseInt(id, 16);
};

export const convertDashToUnderscore = (str: string) => {
	return str.replace(/-/g, '_');
};

const avatarVariants = new Set<AvatarColorVariant>([
	AvatarColorVariant.COLOR_GREEN,
	AvatarColorVariant.COLOR_CYAN,
	AvatarColorVariant.COLOR_LIGHTBLUE,
	AvatarColorVariant.COLOR_DARKBLUE,
	AvatarColorVariant.COLOR_PURPLE,
	AvatarColorVariant.COLOR_PINK,
	AvatarColorVariant.COLOR_RED,
	AvatarColorVariant.COLOR_PEACH,
	AvatarColorVariant.GRADIENT_BLUE_LIGHT,
	AvatarColorVariant.GRADIENT_BLUE_PURPLE,
	AvatarColorVariant.GRADIENT_CYAN_LIGHT,
	AvatarColorVariant.GRADIENT_PINK_PURPLE,
	AvatarColorVariant.GRADIENT_GREEN_YELLOW,
	AvatarColorVariant.GRADIENT_CYAN_BLUE,
	AvatarColorVariant.GRADIENT_ORANGE_LIGHT,
	AvatarColorVariant.GRADIENT_YELLOW_GOLD,
]);

export const checkIfColorIsAvatarVariant = (color: string): boolean => {
	return avatarVariants.has(color as AvatarColorVariant);
};

/**
 * Converts legacy avatar color format (e.g., 'color-avatar-1') to AvatarColorVariant enum values.
 * This function is needed for backward compatibility with old data stored in the database.
 * @param color - Legacy color string or already converted AvatarColorVariant
 * @returns AvatarColorVariant enum value or the original value if already converted/unknown
 */
export const convertLegacyAvatarColors = (
	color: string | undefined | null,
): AvatarColorVariant | string | undefined | null => {
	if (!color || typeof color !== 'string') {
		return color;
	}

	// If already in the new format, return as is
	if (checkIfColorIsAvatarVariant(color)) {
		return color as AvatarColorVariant;
	}

	// Convert legacy format
	switch (color) {
		case 'color-avatar-1':
			return AvatarColorVariant.COLOR_GREEN;
		case 'color-avatar-2':
			return AvatarColorVariant.COLOR_CYAN;
		case 'color-avatar-3':
			return AvatarColorVariant.COLOR_LIGHTBLUE;
		case 'color-avatar-4':
			return AvatarColorVariant.COLOR_DARKBLUE;
		case 'color-avatar-5':
			return AvatarColorVariant.COLOR_PURPLE;
		case 'color-avatar-6':
			return AvatarColorVariant.COLOR_PINK;
		case 'color-avatar-7':
			return AvatarColorVariant.COLOR_RED;
		case 'color-avatar-8':
			return AvatarColorVariant.COLOR_PEACH;
		case 'color-avatar-9':
			return AvatarColorVariant.GRADIENT_BLUE_LIGHT;
		case 'color-avatar-10':
			return AvatarColorVariant.GRADIENT_BLUE_PURPLE;
		case 'color-avatar-11':
			return AvatarColorVariant.GRADIENT_CYAN_LIGHT;
		case 'color-avatar-12':
			return AvatarColorVariant.GRADIENT_PINK_PURPLE;
		case 'color-avatar-13':
			return AvatarColorVariant.GRADIENT_GREEN_YELLOW;
		case 'color-avatar-14':
			return AvatarColorVariant.GRADIENT_CYAN_BLUE;
		case 'color-avatar-15':
			return AvatarColorVariant.GRADIENT_ORANGE_LIGHT;
		case 'color-avatar-16':
			return AvatarColorVariant.GRADIENT_YELLOW_GOLD;
		default:
			// Return original if not a legacy format
			return color;
	}
};

export const getLimitedValue = (value, limit = 100) => {
	return value ? Math.min(value, limit) : 0;
};

export const formatTimeLeft = (secondsLeft: number) => {
	const SECONDS_IN_MINUTE = 60;
	const SECONDS_IN_HOUR = 3600;
	const SECONDS_IN_DAY = SECONDS_IN_HOUR * 24;
	const SECONDS_IN_MONTH = SECONDS_IN_DAY * (365 / 12);
	const SECONDS_IN_YEAR = SECONDS_IN_DAY * 365;

	const years = Math.floor(secondsLeft / SECONDS_IN_YEAR);
	secondsLeft %= SECONDS_IN_YEAR;

	const months = Math.floor(secondsLeft / SECONDS_IN_MONTH);
	secondsLeft %= SECONDS_IN_MONTH;

	const days = Math.floor(secondsLeft / SECONDS_IN_DAY);
	secondsLeft %= SECONDS_IN_DAY;

	const hours = Math.floor(secondsLeft / SECONDS_IN_HOUR);
	secondsLeft %= SECONDS_IN_HOUR;

	const minutes = Math.floor(secondsLeft / SECONDS_IN_MINUTE);

	const seconds = Math.floor(secondsLeft % SECONDS_IN_MINUTE);

	let unit;
	let parts = [
		{
			value: '',
			type: 'primary',
			icon: 'fas fa-clock',
		},
	];

	if (years) {
		parts.push({
			icon: '',
			type: 'primary',
			value: `${years} ${get(format)('option_year', { default: 'year' }).toLowerCase()}`,
		});
	}

	if (months) {
		unit = (
			months === 1
				? get(format)('option_month', { default: 'month' })
				: get(format)('option_months', { default: 'months' })
		).toLowerCase();
		parts.push({
			icon: '',
			type: 'primary',
			value: `${months} ${unit}`,
		});
	}

	if (days) {
		unit = (
			days === 1
				? get(format)('option_day', { default: 'day' })
				: get(format)('label_days', { default: 'days' })
		).toLowerCase();
		parts.push({
			icon: '',
			type: 'primary',
			value: `${days} ${unit}`,
		});
	}

	if (hours) {
		unit = (
			hours === 1
				? get(format)('select_units_hour', { default: 'hour' })
				: get(format)('select_units_hours', { default: 'hours' })
		).toLowerCase();
		parts.push({
			icon: '',
			type: 'primary',
			value: `${hours} ${unit}`,
		});
	}

	if (minutes) {
		unit = (
			minutes === 1
				? get(format)('select_units_minute', { default: 'minute' })
				: get(format)('select_units_minutes', { default: 'minutes' })
		).toLowerCase();
		parts.push({
			icon: '',
			type: 'primary',
			value: `${minutes} ${unit}`,
		});
	}

	if (seconds || parts.length === 0) {
		unit = (
			seconds === 1
				? get(format)('select_units_second', { default: 'second' })
				: get(format)('select_units_seconds', { default: 'seconds' })
		).toLowerCase();
		parts.push({
			icon: '',
			type: 'primary',
			value: `${seconds} ${unit}`,
		});
	}

	return parts;
};
export function rgbToCss(color: CameraZone['color'], alpha: number = 0.3): string {
	if (!color) return 'rgba(0,0,0,0)';
	return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
}

/**
 * Filters scenes data to extract only alarm-related scenes for mobile platforms.
 * Used by both iOS and Android mobile list updates.
 * @param scenes - Object containing scene data
 * @returns Filtered object containing only alarm scenes with their essential properties
 */
export function filter_mobile_alarms_data(scenes) {
	const filtered_alarms = {};
	for (const k in scenes) {
		if (Object.prototype.hasOwnProperty.call(scenes, k)) {
			const v = scenes[k];
			if (
				v.hasOwnProperty('_gui_function') &&
				v._gui_function === 'alarm' &&
				!v.hasOwnProperty('tschem')
			) {
				filtered_alarms[k] = {};
				filtered_alarms[k].room = v._meta?.room?.toString();
				filtered_alarms[k].name = v._meta?.name || '';
				filtered_alarms[k].id = v._id.toString();
				filtered_alarms[k].position = v._meta?.position || 0;
				filtered_alarms[k].pin_lock = v?.pin_lock || false;
			}
		}
	}
	return filtered_alarms;
}

/**
 * Replaces empty unit names in the available units with a translated fallback.
 *
 * The first home created for an account has no name set server-side, resulting in
 * an empty string in `available_units`. This function normalises the data before it
 * is written to the store so every consumer sees a human-readable name.
 *
 * Supports two input formats:
 * - `AvailableUnits`: Record<string, string> (unit id → name map)
 * - `AvailableUnitInfo[]`: Array of unit objects with unit_id, name, premium, avatar
 *
 * Always returns `AvailableUnits` (Record<string, string>) for store compatibility.
 *
 * @param units - The raw units data received from the server.
 * @returns A record with unit id as key and name as value, with empty names replaced
 *          by the translated value of `settings_default_home_name` (default: "My Home").
 * @deprecated - This function is deprecated and will be removed in the future.
 */
export function fillMissingUnitNames(units: AvailableUnits | AvailableUnitInfo[]): AvailableUnits {
	if (!units) {
		return {};
	}

	if (Array.isArray(units)) {
		return Object.fromEntries(units.map((unit) => [String(unit.unit_id), unit.name?.trim()]));
	}

	return Object.fromEntries(Object.entries(units).map(([id, name]) => [id, name?.trim()]));
}
